// Company stores (S3, 2026-10-12) — loads the signed-in person's store
// (GET /portal/store) for the /store pages. The server decides everything:
// which store, and whether they are its admin. No store → a plain message;
// an admin page opened by a non-admin → back to /store.
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiMessage, fetchMyStore, MY_STORE_KEY, type MyStore } from "@/lib/portal-store";

export function useMyStore() {
  return useQuery({ queryKey: MY_STORE_KEY, queryFn: fetchMyStore, retry: false });
}

export function StoreGate({
  admin = false,
  children,
}: {
  admin?: boolean;
  children: (store: MyStore) => ReactNode;
}) {
  const q = useMyStore();
  if (q.isPending) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-10 w-10 animate-pulse rounded-md bg-cream-300" aria-label="Loading" />
      </div>
    );
  }
  if (q.isError) {
    return <p className="rounded-lg border border-rule bg-white p-6 text-sm text-rust-500">{apiMessage(q.error)}</p>;
  }
  if (!q.data) {
    return (
      <section className="rounded-lg border border-rule bg-white p-8 shadow-sm">
        <h1 className="mb-2 font-display text-2xl font-bold tracking-tight">No company store</h1>
        <p className="text-sm text-brown-400">
          You aren't part of a company store right now. If you think you should be, ask your company's store admin.
        </p>
      </section>
    );
  }
  if (admin && q.data.role !== "admin") return <Navigate to="/store" replace />;
  return <>{children(q.data)}</>;
}

/** The store's logo + name, at the top of every store page. */
export function StoreHeading({ store, title }: { store: MyStore["store"]; title?: string }) {
  return (
    <div className="mb-8 flex items-center gap-4">
      {store.logo?.url && (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-rule bg-white p-2">
          <img src={store.logo.url} alt={`${store.name} logo`} className="max-h-full max-w-full object-contain" />
        </div>
      )}
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{title ?? store.name}</h1>
        {title && <p className="text-sm text-brown-400">{store.name}</p>}
      </div>
    </div>
  );
}
